import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Upload, Save, UserPlus, Trash2, Palette, Pencil, X } from "lucide-react";

const ROLE_TONE = {
  Owner: "bg-anvi-terracotta text-white",
  Manager: "bg-purple-100 text-purple-800",
  Designer: "bg-blue-100 text-blue-800",
  Accounts: "bg-emerald-100 text-emerald-800",
  "Site Supervisor": "bg-amber-100 text-amber-800",
  Client: "bg-anvi-100 text-anvi-700",
};

function BrandTab() {
  const { user } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [form, setForm] = useState({ name: "", tagline: "", address: "", gstin: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const canEdit = ["Owner", "Manager"].includes(user?.role);

  const load = useCallback(async () => {
    const r = await api.tenantMe();
    setTenant(r.data);
    setForm({
      name: r.data.name || "",
      tagline: r.data.tagline || "",
      address: r.data.address || "",
      gstin: r.data.gstin || "",
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.tenantBrandUpdate(form);
      toast.success("Brand saved · sidebar will refresh on next reload");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await api.tenantLogoUpload(file);
      toast.success("Logo uploaded");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  if (!tenant) return <div className="text-anvi-500">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="brand-tab">
      <div className="bg-white border border-anvi-200 rounded-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Logo</div>
            <div className="font-heading text-lg text-anvi-900 mt-0.5">Firm mark · appears on quotations & PDFs</div>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="w-24 h-24 rounded-md border border-anvi-200 flex items-center justify-center bg-anvi-50">
            {tenant.logo_path ? (
              <img src={api.tenantLogoUrl(tenant.id)} alt="logo" className="max-w-full max-h-full object-contain" data-testid="brand-logo-preview" />
            ) : (
              <span className="font-heading text-3xl text-anvi-400">{(tenant.name || "A")[0]}</span>
            )}
          </div>
          <div className="flex-1">
            <input ref={fileRef} type="file" accept="image/*" onChange={uploadLogo} className="hidden" data-testid="brand-logo-input" />
            <button onClick={() => fileRef.current.click()} disabled={!canEdit || uploading} data-testid="brand-logo-upload"
                    className="flex items-center gap-2 bg-anvi-900 hover:bg-anvi-800 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50">
              <Upload size={14} strokeWidth={1.5} /> {uploading ? "Uploading…" : (tenant.logo_path ? "Replace logo" : "Upload logo")}
            </button>
            <div className="text-xs text-anvi-500 mt-2">PNG or JPG · max 2MB · square works best</div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-anvi-200 rounded-md p-6">
        <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Firm Identity</div>
        <div className="font-heading text-lg text-anvi-900 mt-0.5 mb-4">These appear on login, sidebar, and every PDF</div>
        <div className="space-y-4 max-w-2xl">
          {[
            ["Firm name", "name", "text"],
            ["Tagline", "tagline", "text"],
            ["Address", "address", "text"],
            ["GSTIN", "gstin", "text"],
          ].map(([label, k, t]) => (
            <div key={k}>
              <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">{label}</label>
              <input type={t} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                     disabled={!canEdit} data-testid={`brand-${k}`}
                     className="w-full border border-anvi-200 bg-white rounded-md px-3 py-2 text-sm focus:outline-none focus:border-anvi-terracotta disabled:bg-anvi-50 disabled:text-anvi-500" />
            </div>
          ))}
        </div>
        {canEdit && (
          <div className="flex justify-end mt-6">
            <button onClick={save} disabled={saving} data-testid="brand-save"
                    className="flex items-center gap-2 bg-anvi-terracotta hover:bg-anvi-terracotta-dark text-white px-5 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-60">
              <Save size={14} strokeWidth={1.5} /> {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function TeamTab() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", role: "Designer", password: "" });
  const [saving, setSaving] = useState(false);
  const canManage = user?.role === "Owner";

  const load = useCallback(async () => {
    try {
      const r = await api.team();
      setUsers(r.data);
    } catch (err) {
      if (err?.response?.status === 403) setUsers([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.teamInvite(form);
      toast.success(`${form.name} added as ${form.role} · share the login credentials with them`);
      setForm({ name: "", email: "", role: "Designer", password: "" });
      setShowInvite(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invite failed");
    } finally { setSaving(false); }
  };

  const saveEdit = async (patch) => {
    setSaving(true);
    try {
      await api.teamUpdate(editingUser.id, patch);
      toast.success("Team member updated");
      setEditingUser(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name}?`)) return;
    try {
      await api.teamRemove(u.id);
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remove failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="team-tab">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Team</div>
          <div className="font-heading text-xl text-anvi-900 mt-0.5">{users.length} member{users.length === 1 ? "" : "s"}</div>
        </div>
        {canManage && (
          <button onClick={() => setShowInvite(true)} data-testid="invite-btn"
                  className="flex items-center gap-2 bg-anvi-terracotta hover:bg-anvi-terracotta-dark text-white px-4 py-2 rounded-md text-sm font-medium transition-colors">
            <UserPlus size={14} strokeWidth={1.5} /> Invite member
          </button>
        )}
      </div>

      <div className="bg-white border border-anvi-200 rounded-md overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-[10px] tracking-[0.15em] uppercase text-anvi-500 border-b border-anvi-200 bg-anvi-50/50">
              <th className="px-4 py-3 text-left font-medium">Name</th>
              <th className="px-4 py-3 text-left font-medium">Email</th>
              <th className="px-4 py-3 text-left font-medium">Role</th>
              <th className="px-4 py-3 text-left font-medium">Joined</th>
              {canManage && <th className="px-4 py-3 text-right font-medium">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-anvi-100 hover:bg-anvi-50/60" data-testid={`team-row-${u.id}`}>
                <td className="px-4 py-3 text-sm text-anvi-900 font-medium">{u.name}</td>
                <td className="px-4 py-3 text-sm text-anvi-500">{u.email}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${ROLE_TONE[u.role] || "bg-anvi-100"}`}>{u.role}</span>
                </td>
                <td className="px-4 py-3 text-xs text-anvi-500 tabular-nums">{new Date(u.created_at).toLocaleDateString("en-IN")}</td>
                {canManage && (
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => setEditingUser(u)} data-testid={`team-edit-${u.id}`}
                              className="text-anvi-500 hover:text-anvi-terracotta hover:bg-anvi-terracotta/10 p-1.5 rounded-md" title="Edit">
                        <Pencil size={14} strokeWidth={1.5} />
                      </button>
                      {u.id !== user.id && (
                        <button onClick={() => remove(u)} data-testid={`team-remove-${u.id}`}
                                className="text-anvi-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded-md" title="Remove">
                          <Trash2 size={14} strokeWidth={1.5} />
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan={canManage ? 5 : 4} className="text-center py-10 text-sm text-anvi-500">
                {canManage ? "No team members yet — invite your first designer" : "Access restricted — only Owner/Manager can see team"}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showInvite && (
        <div className="fixed inset-0 bg-anvi-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowInvite(false)}>
          <div className="bg-white rounded-md w-full max-w-md border border-anvi-200 shadow-lg" onClick={(e) => e.stopPropagation()} data-testid="invite-modal">
            <div className="px-6 py-4 border-b border-anvi-200">
              <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">New Team Member</div>
              <div className="font-heading text-xl text-anvi-900 mt-0.5">Invite by role</div>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <div>
                <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Full name</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                       data-testid="invite-name" className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-anvi-terracotta" />
              </div>
              <div>
                <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Email</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                       data-testid="invite-email" className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-anvi-terracotta" />
              </div>
              <div>
                <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Role</label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                        data-testid="invite-role" className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-anvi-terracotta">
                  {["Designer", "Manager", "Accounts", "Site Supervisor", "Client"].map((r) => <option key={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Initial password (min 6)</label>
                <input required type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                       minLength={6} data-testid="invite-password"
                       placeholder="Share this with the invitee"
                       className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-anvi-terracotta" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowInvite(false)} className="px-4 py-2 text-sm text-anvi-700 hover:bg-anvi-100 rounded-md">Cancel</button>
                <button type="submit" disabled={saving} data-testid="invite-submit"
                        className="px-4 py-2 text-sm bg-anvi-terracotta text-white hover:bg-anvi-terracotta-dark rounded-md disabled:opacity-60">
                  {saving ? "Inviting…" : "Add member"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingUser && (
        <EditUserModal user={editingUser} onClose={() => setEditingUser(null)} onSave={saveEdit} saving={saving} />
      )}
    </div>
  );
}

function EditUserModal({ user, onClose, onSave, saving }) {
  const [form, setForm] = useState({
    name: user.name,
    email: user.email,
    role: user.role,
    password: "",
  });
  const submit = (e) => {
    e.preventDefault();
    const patch = {};
    if (form.name !== user.name) patch.name = form.name;
    if (form.email !== user.email) patch.email = form.email;
    if (form.role !== user.role) patch.role = form.role;
    if (form.password) patch.password = form.password;
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    onSave(patch);
  };
  return (
    <div className="fixed inset-0 bg-anvi-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-md border border-anvi-200 shadow-lg" onClick={(e) => e.stopPropagation()} data-testid="edit-user-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-anvi-200">
          <div>
            <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Edit member</div>
            <div className="font-heading text-xl text-anvi-900 mt-0.5">{user.name}</div>
          </div>
          <button onClick={onClose} className="text-anvi-500 hover:text-anvi-900 p-1"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Full name</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   data-testid="edit-name" className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-anvi-terracotta" />
          </div>
          <div>
            <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Email</label>
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                   data-testid="edit-email" className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-anvi-terracotta" />
          </div>
          <div>
            <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Role</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                    data-testid="edit-role" className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-anvi-terracotta">
              {["Owner", "Manager", "Designer", "Accounts", "Site Supervisor", "Client"].map((r) => <option key={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Reset password (optional)</label>
            <input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                   placeholder="Leave blank to keep current"
                   data-testid="edit-password"
                   className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-anvi-terracotta" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-anvi-700 hover:bg-anvi-100 rounded-md">Cancel</button>
            <button type="submit" disabled={saving} data-testid="edit-submit"
                    className="px-4 py-2 text-sm bg-anvi-terracotta text-white hover:bg-anvi-terracotta-dark rounded-md disabled:opacity-60">
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Settings() {
  const [tab, setTab] = useState("brand");
  return (
    <div className="px-10 pt-4 pb-10 animate-fade-in" data-testid="settings-page">
      <div className="mb-8">
        <div className="text-xs tracking-[0.25em] uppercase text-anvi-500 mb-2">Configuration</div>
        <h1 className="font-heading text-4xl text-anvi-900">Settings</h1>
      </div>
      <div className="flex gap-1 border-b border-anvi-200 mb-6">
        {[
          { key: "brand", label: "Brand", icon: Palette },
          { key: "team", label: "Team", icon: UserPlus },
        ].map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`settings-tab-${t.key}`}
                  className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px flex items-center gap-2 ${tab === t.key ? "border-anvi-terracotta text-anvi-terracotta" : "border-transparent text-anvi-500 hover:text-anvi-900"}`}>
            <t.icon size={14} strokeWidth={1.5} /> {t.label}
          </button>
        ))}
      </div>
      {tab === "brand" && <BrandTab />}
      {tab === "team" && <TeamTab />}
    </div>
  );
}
